import { isEmpty, isNil, omitBy } from 'lodash';

import graphqlClient from '../graphql';
import { relayFaxDocumentLinksGql, relayOrderDetailsGql, relayOrdersGql } from './gql';

export const RELAY_ORDERS_PAGE_SIZE = 25;

const SAFE_MESSAGE = 'The relayed orders request failed.';

/**
 * POM-6279 P5. A deliveryMethods value outside the enum is refused, and the refusal names the API
 * field. The operator set it through the Relay Type control, so that is what this names.
 */
export const RELAY_FILTER_INVALID_MESSAGE =
  'The Relay Type filter has a value this queue does not recognize. Clear it and try again.';

/** Operator wording for the codes whose core message speaks API rather than screen. */
const MESSAGE_BY_CODE = {
  RELAY_ORDERS_FILTER_INVALID: RELAY_FILTER_INVALID_MESSAGE,
};

/**
 * The LIST query's own extra wording. An out-of-enum deliveryMethods VARIABLE never reaches the
 * resolver -- GraphQL coercion refuses it first as BAD_USER_INPUT, so RELAY_ORDERS_FILTER_INVALID
 * (kept above) only fires if the input is ever loosened. The filter is the only operator-shaped
 * input on relayOrders, so there BAD_USER_INPUT is the Relay Type filter speaking. Not applied to
 * the detail read, where no filter is involved.
 */
const LIST_MESSAGE_BY_CODE = {
  BAD_USER_INPUT: RELAY_FILTER_INVALID_MESSAGE,
};

/**
 * Strips a graphql-request failure down to what the error handler actually displays.
 *
 * WHY THIS EXISTS, and why it lives at the relay call sites specifically: graphql-request v6
 * builds ClientError's message as
 *   `${extractMessage(response)}: ${JSON.stringify({ response, request })}`
 * so the message — and therefore the stack — embeds the ENTIRE serialized response and the request
 * variables. `handleError` hands that object to Sentry.captureException and writes `error.stack`
 * into the error slice. On a partial-data GraphQL error against relayOrderDetails, that ships the
 * patient's ship-to address and every eventData blob (including MANUAL_NOTE operator free text) to
 * an error reporter.
 *
 * The raw-error-to-Sentry path is platform-wide and pre-existing, but relay is the first caller to
 * point a PHI-dense query at it, so the relay call sites are where it gets closed. `handleError`
 * reads only `response.errors[*].message` / `.extensions` and the top-level message, all of which
 * survive here; the data and the variables do not.
 */
export const toSafeRelayError = (error, messageByCode = {}) => {
  const errors = (error?.response?.errors ?? []).map(({ message, extensions }) => ({
    message: messageByCode[extensions?.code] ?? MESSAGE_BY_CODE[extensions?.code] ?? message,
    extensions,
  }));

  const safe = new Error(errors[0]?.message || SAFE_MESSAGE);

  safe.name = error?.name ?? 'Error';
  // No `data`, no `request`: a new Error's stack is built here, not from the vendor's message.
  safe.response = { errors };

  return safe;
};

/**
 * Absent keys and EMPTY lists are both dropped: core reads an empty deliveryMethods as "no
 * filter" anyway, and sending one shape for "unfiltered" keeps the request honest about it.
 */
const toWireFilter = (filter) =>
  omitBy(filter, (value) => isNil(value) || (Array.isArray(value) && isEmpty(value)));

export const listRelayOrdersApi = ({
  page = 1,
  limit = RELAY_ORDERS_PAGE_SIZE,
  filter,
  sortBy,
} = {}) => {
  const offset = (page - 1) * limit;

  return graphqlClient
    .request(relayOrdersGql, { limit, offset, filter: toWireFilter(filter), sortBy })
    .then(({ relayOrders }) => relayOrders)
    .catch((error) => {
      throw toSafeRelayError(error, LIST_MESSAGE_BY_CODE);
    });
};

/** POM-6279. What the patient page's Relay Orders tab says when its read fails. */
export const PATIENT_RELAY_ORDERS_FAILED_MESSAGE =
  "This patient's relayed orders could not be loaded.";

/**
 * The patient read's own wording. The queue's map names the Relay Type filter for both codes, and
 * that is wrong here: the only filter on this read is the patient, which the operator did not
 * type and cannot clear. A refusal of it means the page's patient id is unusable.
 */
const PATIENT_LIST_MESSAGE_BY_CODE = {
  RELAY_ORDERS_FILTER_INVALID: PATIENT_RELAY_ORDERS_FAILED_MESSAGE,
  BAD_USER_INPUT: PATIENT_RELAY_ORDERS_FAILED_MESSAGE,
};

/**
 * POM-6279. One patient's relay orders -- the queue's query, unchanged, narrowed by
 * RelayOrdersFilter.patientId (an exact MPI match). TRANSFERRED only.
 *
 * FAILS CLOSED on a missing patient, before any request is made. relayOrders with no filter is
 * the whole queue, so a call that lost its patient id would put every patient's relayed orders on
 * one patient's page. Rejecting here keeps that from depending on every caller remembering.
 */
export const listPatientRelayOrdersApi = ({ patientId, page = 1 } = {}) => {
  if (!patientId) {
    return Promise.reject(new Error(PATIENT_RELAY_ORDERS_FAILED_MESSAGE));
  }

  const limit = RELAY_ORDERS_PAGE_SIZE;
  const offset = (page - 1) * limit;

  return graphqlClient
    .request(relayOrdersGql, { filter: { patientId, statuses: ['TRANSFERRED'] }, limit, offset })
    .then(({ relayOrders }) => relayOrders)
    .catch((error) => {
      throw toSafeRelayError(error, PATIENT_LIST_MESSAGE_BY_CODE);
    });
};

/**
 * Resolves to null when no relay order carries that id — core answers null rather than raising,
 * including for an id that is not a uuid, so the caller has one "not found" shape to handle.
 */
export const getRelayOrderDetailsApi = (relayOrderId) =>
  graphqlClient
    .request(relayOrderDetailsGql, { relayOrderId })
    .then(({ relayOrderDetails }) => relayOrderDetails ?? null)
    .catch((error) => {
      throw toSafeRelayError(error);
    });

/** POM-6279. Download links for a FAX relay's PDFs; an empty list means none was sent yet. */
export const getRelayFaxDocumentLinksApi = (relayOrderId) =>
  graphqlClient
    .request(relayFaxDocumentLinksGql, { relayOrderId })
    .then(({ relayFaxDocumentLinks }) => relayFaxDocumentLinks ?? [])
    .catch((error) => {
      throw toSafeRelayError(error);
    });
